import { Icon, Tooltip } from 'components/common';
import { useState, useMemo } from 'react';
import { sheetName as strings } from '../strings';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';
import { EditorRow, SmallText } from '../styled';
import { RevertableInput } from './RevertableInput';
import { createSheetNameValidator } from './validation';

const editIconStyle = {
  cursor: 'pointer',
  color: '#8d8d8d',
  fontSize: 12,
  marginLeft: 4,
};

/**
 * A specialised input for editing the name of a sheet within the sheets list.
 * @param {Object} props
 * @param {string} props.sheetMetadataId
 * @param {boolean} props.enabled
 */
export const SheetNameInput = ({ sheetMetadataId, enabled }) => {
  // ========== Redux state ==========

  const dispatch = useDispatch();
  const sheetDefaultName = useSelector(state =>
    selectors.getSheetDefaultName(state, sheetMetadataId)
  );
  const sheetStoreName = useSelector(state =>
    selectors.getEditingSheetName(state, sheetMetadataId)
  );
  const otherSheetNames = useSelector(state =>
    selectors.getOtherSheetNames(state, sheetMetadataId)
  );

  // ========== Derived state ==========

  const validateSheetName = useMemo(
    () => createSheetNameValidator(otherSheetNames),
    [otherSheetNames]
  );

  // ========== Local UI state ==========

  const [editingSheetName, setEditingSheetName] = useState(false);

  // ========== Event handlers ==========

  /** @param {string} newValue */
  const onNewValueConfirmed = newValue => {
    const overridenName =
      newValue && newValue !== sheetDefaultName ? newValue : null;
    dispatch(
      exportTemplateActions.editingSheetNameChanged({
        sheetId: sheetMetadataId,
        overridenName,
      })
    );
    setEditingSheetName(false);
  };

  // ========== Read-only render ==========

  if (!editingSheetName) {
    return (
      <EditorRow $bold>
        <Tooltip
          title={
            sheetStoreName && sheetStoreName !== sheetDefaultName
              ? strings.originalName(sheetDefaultName)
              : ''
          }
        >
          {sheetStoreName}
        </Tooltip>
        {sheetStoreName && sheetStoreName !== sheetDefaultName && (
          <SmallText $mt={2}>{strings.edited}</SmallText>
        )}
        {enabled && (
          <span style={editIconStyle} onClick={() => setEditingSheetName(true)}>
            <Icon icon='edit' size='1x' color='#8D8D8D' />
          </span>
        )}
      </EditorRow>
    );
  }

  // ========== Editing render ==========

  return (
    <EditorRow $bold>
      <RevertableInput
        currentValue={sheetStoreName}
        revertValue={sheetDefaultName}
        onNewValueConfirmed={onNewValueConfirmed}
        validate={validateSheetName}
      />
    </EditorRow>
  );
};
